/** MIDI phrases are selected by time window and scheduled through WebAudio. */
import type { AudioBus } from './audio.js';
import { parseMidi, type MidiNote, type MidiSong } from './midi.js';
export const SEG_TITLE = 0;
export const SEG_RESULT = 1;
export const SEG_HIT = 2;
export const SEG_CRASH = 3;
export const SEG_FLIGHT = 4;
export const SEG_FALL = 5;
interface Segment {
  startUs: number;
  stopUs: number;
  kind: number;
}
function voiceFor(
  program: number,
  channel: number,
): 'brass' | 'organ' | 'keys' | 'square' | 'drums' {
  if (channel === 9) return 'drums';
  if (program >= 56 && program <= 79) return 'brass';
  if (program >= 16 && program <= 23) return 'organ';
  if (program <= 15) return 'keys';
  return 'square';
}
const CHANNEL_GAIN: Record<number, number> = { 0: 0.5, 1: 0.38, 2: 0.26, 9: 0.34 };
export class MusicPlayer {
  private song: MidiSong | null = null;
  private segments: Segment[] = [];
  private lastSlot = -1;
  private playingSlot = -1;
  private stopTimer: ReturnType<typeof setTimeout> | null = null;
  private voices: {
    osc?: OscillatorNode;
    src?: AudioBufferSourceNode;
    gain: GainNode;
  }[] = [];
  private noiseBuf: AudioBuffer | null = null;
  constructor(private readonly bus: AudioBus) {}
  get currentSlot(): number {
    return this.playingSlot;
  }
  async load(
    url = '/music/theme.mid',
    segments: [number, number][] = [],
    kinds: number[] = [],
  ): Promise<void> {
    try {
      const res = await fetch(url);
      if (!res.ok) return;
      this.song = parseMidi(new Uint8Array(await res.arrayBuffer()));
      this.segments = segments.map(([startUs, stopUs], i) => ({
        startUs,
        stopUs,
        kind: kinds[i] ?? 0,
      }));
    } catch {}
  }
  play(slot: number): boolean {
    if (!this.bus.enabled || !this.song || slot < 0 || slot >= this.segments.length) return false;
    const seg = this.segments[slot]!;
    if (seg.kind === -1) return false;
    if (seg.kind === 2) return false;
    const out = this.bus.output();
    if (!out) return false;
    if (this.playingSlot >= 0 && !(this.lastSlot === 1 && slot !== 1)) return false;
    this.stop();
    this.lastSlot = slot;
    this.playingSlot = slot;
    const { ctx, dest } = out;
    const segGain = ctx.createGain();
    segGain.gain.value = 1;
    segGain.connect(dest);
    const t0 = ctx.currentTime;
    for (const note of this.song.notes) {
      if (note.startUs < seg.startUs || note.startUs >= seg.stopUs) continue;
      const at = t0 + (note.startUs - seg.startUs) / 1e6;
      const dur = Math.min(note.durUs, seg.stopUs - note.startUs) / 1e6;
      this.scheduleNote(ctx, segGain, note, at, Math.max(0.001, dur));
    }
    this.stopTimer = setTimeout(() => this.stop(), (seg.stopUs - seg.startUs) / 1000);
    return true;
  }
  stop(): void {
    if (this.stopTimer !== null) {
      clearTimeout(this.stopTimer);
      this.stopTimer = null;
    }
    for (const v of this.voices) {
      try {
        v.gain.gain.cancelScheduledValues(0);
        v.gain.gain.value = 0;
        v.osc?.stop();
        v.src?.stop();
      } catch {}
    }
    this.voices = [];
    this.playingSlot = -1;
  }
  private scheduleNote(
    ctx: AudioContext,
    dest: AudioNode,
    note: MidiNote,
    at: number,
    dur: number,
  ): void {
    const voice = voiceFor(this.song?.programs[note.channel] ?? 0, note.channel);
    const gain = ctx.createGain();
    gain.connect(dest);
    const vel = Math.pow(note.velocity / 127, 1.4);
    const base = (CHANNEL_GAIN[note.channel] ?? 0.3) * vel;
    const freq = 440 * Math.pow(2, (note.note - 69) / 12);
    if (voice === 'drums') {
      this.scheduleDrum(ctx, gain, note.note, at, base);
      return;
    }
    const osc = ctx.createOscillator();
    if (voice === 'brass') {
      osc.type = 'sawtooth';
      osc.frequency.value = freq;
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = Math.min(8000, freq * 4 + 900);
      osc.connect(lp);
      lp.connect(gain);
    } else if (voice === 'organ') {
      osc.type = 'square';
      osc.frequency.value = freq;
      const sub = ctx.createOscillator();
      sub.type = 'sine';
      sub.frequency.value = freq / 2;
      const subGain = ctx.createGain();
      subGain.gain.value = 0.5;
      sub.connect(subGain);
      subGain.connect(gain);
      osc.connect(gain);
      this.voices.push({ osc: sub, gain: subGain });
      sub.start(at);
      sub.stop(at + dur + 0.15);
    } else if (voice === 'keys') {
      osc.type = 'triangle';
      osc.frequency.value = freq;
      osc.connect(gain);
    } else {
      osc.type = 'square';
      osc.frequency.value = freq;
      osc.connect(gain);
    }
    const attack = voice === 'brass' ? 0.025 : voice === 'organ' ? 0.005 : 0.004;
    const release = voice === 'keys' ? 0.12 : 0.05;
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(base, at + attack);
    if (voice === 'keys')
      gain.gain.exponentialRampToValueAtTime(Math.max(0.001, base * 0.4), at + Math.min(dur, 0.4));
    gain.gain.setValueAtTime(voice === 'keys' ? Math.max(0.001, base * 0.4) : base, at + dur);
    gain.gain.linearRampToValueAtTime(0, at + dur + release);
    osc.start(at);
    osc.stop(at + dur + release + 0.02);
    this.voices.push({ osc, gain });
  }
  private scheduleDrum(
    ctx: AudioContext,
    dest: AudioNode,
    note: number,
    at: number,
    base: number,
  ): void {
    const noise = this.noise(ctx);
    const g = ctx.createGain();
    g.connect(dest);
    if (note === 35 || note === 36 || note === 41 || note === 43 || note === 45) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(note <= 36 ? 110 : 170, at);
      osc.frequency.exponentialRampToValueAtTime(45, at + 0.11);
      g.gain.setValueAtTime(base * 1.1, at);
      g.gain.exponentialRampToValueAtTime(0.001, at + 0.16);
      osc.connect(g);
      osc.start(at);
      osc.stop(at + 0.18);
      this.voices.push({ osc, gain: g });
      return;
    }
    const src = ctx.createBufferSource();
    src.buffer = noise;
    const filter = ctx.createBiquadFilter();
    const hat = note === 42 || note === 44 || note === 46 || note >= 55;
    filter.type = hat ? 'highpass' : 'bandpass';
    filter.frequency.value = hat ? 6500 : 1800;
    const dur = hat ? 0.05 : 0.13;
    g.gain.setValueAtTime(hat ? base * 0.5 : base, at);
    g.gain.exponentialRampToValueAtTime(0.001, at + dur);
    src.connect(filter);
    filter.connect(g);
    src.start(at);
    src.stop(at + dur + 0.02);
    this.voices.push({ src, gain: g });
  }
  private noise(ctx: AudioContext): AudioBuffer {
    if (!this.noiseBuf) {
      const len = Math.floor(ctx.sampleRate * 0.25);
      this.noiseBuf = ctx.createBuffer(1, len, ctx.sampleRate);
      const ch = this.noiseBuf.getChannelData(0);
      for (let i = 0; i < len; i++) ch[i] = Math.random() * 2 - 1;
    }
    return this.noiseBuf;
  }
}
